import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import type { StaticImageData } from 'Components/Image';
import { Typography } from 'Components/Typography';

import { moduleFullRoutine } from 'assets/content/landingPages/supplements';
import { ReactComponent as Consultation } from 'assets/images/landingPages/supplements/consultation.svg';
import { ReactComponent as Routine } from 'assets/images/landingPages/supplements/routine.svg';
import { ReactComponent as Subscribe } from 'assets/images/landingPages/supplements/subscribe.svg';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import { CTA } from './CTA';

const Root = styled.section<{
  img: { mobile: StaticImageData; tablet: StaticImageData; desktop: StaticImageData };
}>`
  width: 100vw;
  height: 1100px;
  padding: 80px 24px;

  background-image: ${({ img }) => `url(${img.mobile.src})`};
  background-size: cover;
  background-position: bottom;
  border-bottom: 2px solid ${theme.colors.primary[400]};

  ${legacyTheme.breakpoints.up('sm')} {
    height: 1288px;
    padding: 118px 72px;
    background-image: ${({ img }) => `url(${img.tablet.src})`};
  }

  ${legacyTheme.breakpoints.up('md')} {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;

    height: 100%;
    padding: 100px 135px 140px;

    background-image: ${({ img }) => `url(${img.desktop.src})`};
    background-position: bottom left;
  }
`;

const Header = styled.div`
  margin-bottom: 48px;

  ${legacyTheme.breakpoints.up('sm')} {
    margin-bottom: 64px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    width: 500px;
    margin-right: 50px;
    margin-bottom: 0;
  }
`;

const Title = styled(Typography)`
  margin: 20px 0 16px;
  font-size: 36px;
  line-height: 1.17;
  letter-spacing: 0.25px;

  ${legacyTheme.breakpoints.up('sm')} {
    margin: 36px 0 32px;
    font-size: 60px;
    letter-spacing: normal;
  }

  ${legacyTheme.breakpoints.up('md')} {
    margin-bottom: 24px;
  }
`;

const Paragraph = styled(Typography)`
  line-height: 1.38;
  letter-spacing: normal;

  ${legacyTheme.breakpoints.up('sm')} {
    line-height: 1.5;
    letter-spacing: 0.5px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    max-width: 550px;
  }
`;

const Steps = styled.div`
  ${legacyTheme.breakpoints.up('md')} {
    width: 500px;
  }
`;

const StepWrapper = styled.div`
  display: flex;
  align-items: center;

  svg {
    margin-right: 15px;
  }
`;

const Wrapper = styled(Typography)`
  margin: 20px 0;

  ${legacyTheme.breakpoints.up('md')} {
    margin: 36px 0;
  }
`;

const Step = styled(Typography)`
  margin-bottom: 8px;

  font-size: 22px;
  line-height: 1.32;
  letter-spacing: 0.25px;

  ${legacyTheme.breakpoints.up('sm')} {
    margin-bottom: 16px;
    font-size: 24px;
    line-height: 1.21;
  }

  ${legacyTheme.breakpoints.up('md')} {
    margin-bottom: 12px;
  }
`;

const Info = styled(Typography)`
  line-height: 1.5;
  letter-spacing: 0.25px;
`;

export const ModuleFullRoutine = () => {
  const { isMobile } = useResponsiveVariant();

  return (
    <Root data-testid="supplements-landing-module" img={moduleFullRoutine.image}>
      <Header>
        <Title markupName="p" variant="p1">
          {moduleFullRoutine.title}
        </Title>

        <Paragraph markupName="p" variant="p1">
          {moduleFullRoutine.paragraph}
        </Paragraph>
      </Header>

      <Steps>
        {moduleFullRoutine.steps.map((el) => (
          <StepWrapper key={el.step}>
            <div>
              {el.icon === 'consultation' && <Consultation />}
              {el.icon === 'routine' && <Routine />}
              {el.icon === 'subscribe' && <Subscribe />}
            </div>

            <Wrapper>
              <Step markupName="p" variant="p1">
                {el.step}
              </Step>

              <Info markupName="p" variant="p1">
                {el.info}
              </Info>
            </Wrapper>
          </StepWrapper>
        ))}

        {!isMobile && <CTA label={moduleFullRoutine.cta} override />}
      </Steps>
    </Root>
  );
};
